import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  kanbanService,
  mapCommentToUi,
} from '../../../services/kanbanService';
import { sharedService } from '../../../services/sharedService';
import { useAuthStore } from '../../../store/authStore';
import { useKanbanStore } from '../../../store/kanbanStore';
import { isKanbanAssignedOnly } from '../../../utils/rbac';
import {
  CreateKanbanTaskRequest,
  UpdateKanbanTaskRequest,
  TaskPriority,
} from '../../../types/kanban';
import { parseDueDateFromApi } from '../../../utils/taskDate';
import {
  INITIAL_FORM,
  historyFieldLabel,
} from './constants';
import { getTaskLeadRedirectPath } from '../taskLeadNavigation';
import {
  ActivityTab,
  KanbanComment,
  TaskDetailsModalProps,
  TaskFormData,
  TaskHistoryEntry,
  PendingCommentAttachment,
} from './types';
import { RichTextEditor, normalizeRichTextHtml } from '../../../components';
import {
  ActivityPanel,
  ModalHeader,
  TaskInfoCard,
  TaskSidebar,
} from './components';
import './TaskDetailsModal.scss';

export type { TaskDetailsModalMode, TaskDetailsModalProps } from './types';
export {
  ModalHeader,
  TaskInfoCard,
  ActivityPanel,
  TaskSidebar,
  SidebarField,
} from './components';

const mapHistoryToUi = (
  items: Awaited<ReturnType<typeof kanbanService.getHistory>>['items']
): TaskHistoryEntry[] =>
  items.map((item) => {
    const parts: TaskHistoryEntry['parts'] = [];
    if (item.action === 'create' && item.entity === 'comment') {
      parts.push({ type: 'text', value: ' added a comment' });
      if (item.newValue) {
        parts.push({ type: 'link', value: item.newValue.slice(0, 40) });
      }
    } else if (item.action === 'update' && item.fieldName === 'label') {
      if (item.oldValue && item.newValue) {
        parts.push({
          type: 'text',
          value: ` changed label from ${item.oldValue} to ${item.newValue}`,
        });
      } else if (item.newValue) {
        parts.push({ type: 'text', value: ` added label ` });
        parts.push({ type: 'text', value: item.newValue });
      } else if (item.oldValue) {
        parts.push({ type: 'text', value: ` removed label ` });
        parts.push({ type: 'text', value: item.oldValue });
      }
    } else if (item.action === 'update' && item.fieldName) {
      parts.push({
        type: 'text',
        value: ` changed ${item.fieldName} to `,
      });
      parts.push({
        type: item.fieldName === 'status' ? 'status' : item.fieldName === 'priority' ? 'priority' : 'text',
        value: historyFieldLabel(item.fieldName, item.newValue),
      });
    } else if (item.action === 'create' && item.entity === 'task') {
      parts.push({ type: 'text', value: ' created this task' });
    } else {
      parts.push({
        type: 'text',
        value: ` ${item.action} ${item.fieldName ?? item.entity}`,
      });
    }
    return {
      id: String(item.historyId),
      author: item.changedBy.name,
      parts,
      createdAt: item.createdAt,
    };
  });

export const TaskDetailsModal: React.FC<TaskDetailsModalProps> = ({
  isOpen,
  mode,
  task,
  defaultColumnId = 'un_assigned',
  onClose,
  onSave,
  isSaving = false,
}) => {
  const isEditMode = mode === 'edit';
  const role = useAuthStore((s) => s.role);
  const staffUserId = useAuthStore((s) => s.staffUserId ?? s.user?.id ?? null);
  const assignedOnly = isKanbanAssignedOnly(role);
  const isReadOnly =
    assignedOnly &&
    (!isEditMode ||
      !task?.assignee?.id ||
      String(task.assignee.id) !== String(staffUserId));
  const { fetchLookups } = useKanbanStore();

  const [formData, setFormData] = useState<TaskFormData>(INITIAL_FORM);
  const [createSessionKey, setCreateSessionKey] = useState(0);
  const [activeTab, setActiveTab] = useState<ActivityTab>('history');
  const [comments, setComments] = useState<KanbanComment[]>([]);
  const [history, setHistory] = useState<TaskHistoryEntry[]>([]);
  const [newComment, setNewComment] = useState('');
  const [pendingAttachments, setPendingAttachments] = useState<PendingCommentAttachment[]>([]);
  const [isCommentSending, setIsCommentSending] = useState(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [initialLeadId, setInitialLeadId] = useState('');
  const navigate = useNavigate();

  const handleLeadNavigate = useCallback(() => {
    const path = getTaskLeadRedirectPath({
      userId: formData.leadUserId,
      leadId: formData.leadId,
    });
    if (!path) return;
    onClose();
    navigate(path);
  }, [formData.leadId, formData.leadUserId, navigate, onClose]);

  const handleLeadMetaResolved = useCallback(
    (meta: { leadUniqueId?: string | null; leadUserId?: string | null }) => {
      setFormData((prev) => ({
        ...prev,
        leadUniqueId: meta.leadUniqueId ?? prev.leadUniqueId,
        leadUserId: meta.leadUserId ?? prev.leadUserId,
      }));
    },
    []
  );

  useEffect(() => {
    if (!isOpen) return;
    fetchLookups();
  }, [isOpen, fetchLookups]);

  useEffect(() => {
    if (!isOpen) {
      setFormData(INITIAL_FORM);
      setComments([]);
      setHistory([]);
      setNewComment('');
      setPendingAttachments([]);
      setInitialLeadId('');
      setActiveTab('history');
      setIsLoadingDetail(false);
      return;
    }

    const loadDetail = async () => {
      if (isEditMode && task) {
        setIsLoadingDetail(true);
        try {
          const full = await kanbanService.getTaskById(task.taskId);
          const source = full ?? {
            taskId: task.taskId,
            taskNumber: task.taskNumber,
            title: task.title,
            email: task.email,
            description: task.description ?? null,
            status: task.columnId,
            priority: task.priority,
            dueDate: task.dueDate,
            type: task.typeName ? { id: 0, name: task.typeName } : null,
            labels: task.labels,
            assignee: task.assignee
              ? { userId: task.assignee.id, name: task.assignee.name, avatarUrl: task.assignee.avatar }
              : null,
            reporter: task.reporter
              ? { userId: task.reporter.id, name: task.reporter.name }
              : null,
            leadId: task.leadId ?? null,
            leadUniqueId: task.leadUniqueId ?? null,
            userId: task.leadUserId ?? null,
            completedAt: null,
            isClosed: task.isClosed ?? false,
            createdAt: '',
            modifiedAt: '',
          };

          const loadedLeadId = source.leadId != null ? String(source.leadId) : '';
          const loadedLeadUniqueId = source.leadUniqueId ?? '';
          const loadedLeadUserId =
            source.userId != null
              ? String(source.userId)
              : (source as { user_id?: number | string | null }).user_id != null
                ? String((source as { user_id?: number | string | null }).user_id)
                : '';

          setInitialLeadId(loadedLeadId);
          setFormData({
            title: source.title,
            email: source.email,
            description: source.description ?? '',
            leadId: loadedLeadId,
            leadUniqueId: loadedLeadUniqueId,
            leadUserId: loadedLeadUserId,
            columnId: source.status,
            priority: source.priority ?? '',
            taskTypeId: source.type ? String(source.type.id) : '',
            dueDate: parseDueDateFromApi(source.dueDate),
            assigneeId: source.assignee ? String(source.assignee.userId) : '',
            reporterId: source.reporter ? String(source.reporter.userId) : '',
            labelIds: source.labels.map((l) => l.labelId),
          });

          const [commentsRes, historyRes] = await Promise.all([
            kanbanService.getComments(task.taskId),
            kanbanService.getHistory(task.taskId),
          ]);
          setComments(commentsRes.comments.map(mapCommentToUi));
          setHistory(mapHistoryToUi(historyRes.items));
        } finally {
          setIsLoadingDetail(false);
        }
      } else {
        setCreateSessionKey((key) => key + 1);
        const presetLeadId = task?.leadId ?? '';
        const presetLeadUniqueId = task?.leadUniqueId ?? '';
        const presetLeadUserId = task?.leadUserId ?? '';

        setInitialLeadId(presetLeadId);
        setFormData({
          ...INITIAL_FORM,
          columnId: defaultColumnId,
          title: task?.title ?? '',
          email: task?.email ?? '',
          leadId: presetLeadId,
          leadUniqueId: presetLeadUniqueId,
          leadUserId: presetLeadUserId,
        });
        setComments([]);
        setHistory([]);
      }

      setActiveTab(isEditMode ? 'history' : 'comments');
      setNewComment('');
      setPendingAttachments([]);
    };

    loadDetail();
  }, [isOpen, isEditMode, task, defaultColumnId]);

  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSaving) onClose();
    };

    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, isSaving, onClose]);

  const handleChange = useCallback(
    <K extends keyof TaskFormData>(field: K, value: TaskFormData[K]) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const handleSubmit = useCallback(async () => {
    if (isReadOnly || !formData.title.trim()) return;

    const selectedLeadId = formData.leadId ? Number(formData.leadId) : undefined;

    const payload: CreateKanbanTaskRequest = {
      title: formData.title.trim(),
      email: formData.email.trim() || 'contact@example.com',
      description: normalizeRichTextHtml(formData.description) || undefined,
      columnId: formData.columnId,
      priority: formData.priority ? (formData.priority as TaskPriority) : null,
      taskTypeId: formData.taskTypeId ? Number(formData.taskTypeId) : undefined,
      dueDate: formData.dueDate || undefined,
      assigneeId: formData.assigneeId || undefined,
      reporterId: formData.reporterId || undefined,
      labelIds: formData.labelIds.length ? formData.labelIds : undefined,
      leadId: selectedLeadId,
    };

    if (isEditMode && task) {
      const updatePayload: UpdateKanbanTaskRequest = {
        title: payload.title,
        email: payload.email,
        description: payload.description,
        columnId: payload.columnId,
        priority: (formData.priority || null) as TaskPriority | null,
        taskTypeId: formData.taskTypeId ? Number(formData.taskTypeId) : null,
        dueDate: formData.dueDate || null,
        assigneeId: formData.assigneeId || null,
        reporterId: formData.reporterId || null,
        clearAssignee: !formData.assigneeId,
        clearReporter: !formData.reporterId,
        clearPriority: !formData.priority,
        clearDueDate: !formData.dueDate,
        clearTaskType: !formData.taskTypeId,
        labelIds: formData.labelIds,
      };

      if (String(formData.leadId || '') !== String(initialLeadId || '')) {
        if (selectedLeadId) {
          updatePayload.leadId = selectedLeadId;
        } else {
          updatePayload.clearLead = true;
        }
      }

      await onSave(updatePayload as CreateKanbanTaskRequest, task.taskId);
    } else {
      await onSave(payload, task?.taskId);
    }
  }, [formData, initialLeadId, isEditMode, isReadOnly, onSave, task?.taskId]);

  const handleSendComment = useCallback(async () => {
    if (!newComment.trim() || !task?.taskId) return;

    const fileIds = pendingAttachments
      .filter((attachment) => attachment.fileId != null && !attachment.isUploading)
      .map((attachment) => attachment.fileId as number);

    if (pendingAttachments.some((attachment) => attachment.isUploading)) return;

    setIsCommentSending(true);
    try {
      const created = await kanbanService.createComment(
        task.taskId,
        newComment.trim(),
        fileIds.length ? fileIds : undefined
      );
      setComments((prev) => [mapCommentToUi(created), ...prev]);
      setNewComment('');
      setPendingAttachments([]);
      const historyRes = await kanbanService.getHistory(task.taskId);
      setHistory(mapHistoryToUi(historyRes.items));
    } catch (err) {
      console.error('Failed to post comment:', err);
    } finally {
      setIsCommentSending(false);
    }
  }, [newComment, pendingAttachments, task?.taskId]);

  const handleAttachFiles = useCallback(async (files: FileList) => {
    const fileArray = Array.from(files);
    const placeholders: PendingCommentAttachment[] = fileArray.map((file) => ({
      localId: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      fileName: file.name,
      isUploading: true,
    }));

    setPendingAttachments((prev) => [...prev, ...placeholders]);

    try {
      const uploaded = await sharedService.uploadFiles(fileArray);
      setPendingAttachments((prev) =>
        prev.map((attachment) => {
          const index = placeholders.findIndex((item) => item.localId === attachment.localId);
          if (index === -1) return attachment;

          const result = uploaded[index];
          if (!result) {
            return attachment;
          }

          return {
            ...attachment,
            fileId: result.id,
            url: result.url,
            fileName: result.originalName || result.fileName || attachment.fileName,
            isUploading: false,
          };
        })
      );
    } catch (err) {
      console.error('File upload failed:', err);
      setPendingAttachments((prev) =>
        prev.filter(
          (attachment) => !placeholders.some((item) => item.localId === attachment.localId)
        )
      );
    }
  }, []);

  const handleRemovePendingAttachment = useCallback((localId: string) => {
    setPendingAttachments((prev) => prev.filter((attachment) => attachment.localId !== localId));
  }, []);

  const handleDeleteComment = useCallback(
    async (commentId: string) => {
      if (!task?.taskId || !window.confirm('Delete this comment?')) return;
      try {
        await kanbanService.deleteComment(task.taskId, commentId);
        setComments((prev) => prev.filter((c) => c.id !== commentId));
      } catch (err) {
        console.error('Failed to delete comment:', err);
      }
    },
    [task?.taskId]
  );

  if (!isOpen) return null;

  const isValid = !!formData.title.trim();

  return (
    <div className="task-details-modal" role="presentation">
      <div
        className="task-details-modal__overlay"
        onClick={isSaving ? undefined : onClose}
        aria-hidden="true"
      />

      <div
        className="task-details-modal__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="task-details-title"
      >
        <ModalHeader onClose={onClose} disabled={isSaving} />

        <div className="task-details-modal__body">
          {isLoadingDetail ? (
            <p className="task-details-modal__loading">Loading task…</p>
          ) : (
            <div className="task-details-modal__content">
              <div className="task-details-modal__main">
                <TaskInfoCard
                  leadId={formData.leadId}
                  leadUniqueId={formData.leadUniqueId}
                  leadUserId={formData.leadUserId}
                  onLeadClick={handleLeadNavigate}
                  title={formData.title}
                  onTitleChange={(value) => handleChange('title', value)}
                  readOnly={isReadOnly}
                />

                <RichTextEditor
                  key={isEditMode ? `edit-${task?.taskId}` : `create-${createSessionKey}`}
                  label="Task Description"
                  placeholder="Describe the task requirements..."
                  value={formData.description}
                  onChange={(value) => handleChange('description', value)}
                  readOnly={isReadOnly}
                />

                <ActivityPanel
                  activeTab={activeTab}
                  onTabChange={setActiveTab}
                  comments={comments}
                  history={history}
                  newComment={newComment}
                  onNewCommentChange={setNewComment}
                  onSendComment={handleSendComment}
                  onAttachFiles={isEditMode ? handleAttachFiles : undefined}
                  pendingAttachments={pendingAttachments}
                  onRemovePendingAttachment={handleRemovePendingAttachment}
                  isCommentSending={isCommentSending}
                  currentUserId={staffUserId}
                  onDeleteComment={isEditMode ? handleDeleteComment : undefined}
                />
              </div>

              <TaskSidebar
                formData={formData}
                canEditLead={!initialLeadId}
                onChange={handleChange}
                onLeadClick={handleLeadNavigate}
                onLeadMetaResolved={handleLeadMetaResolved}
                onSave={handleSubmit}
                isSaving={isSaving}
                isValid={isValid}
                saveLabel={isEditMode ? 'Save Changes' : 'Create Task'}
                readOnly={isReadOnly}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
